"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.mapDedupedPageStatesToUniversalIssue = exports.mapDedupedResultsToUniversalIssue = exports.mapAxeExtensionIssueToUniversalIssue = exports.mapIssueToSavedIssue = exports.getIssueTags = exports.mapAxeResultsToIssues = void 0;
const axe_core_1 = __importDefault(require("axe-core"));
const descriptions_json_1 = __importDefault(require("@deque/deque-way/dist/bundle/descriptions.json"));
const igt_name_map_1 = require("./igt-name-map");
const build_page_state_key_1 = require("./build-page-state-key");
const WCAG_REGEX = /^wcag\d{1,2}a{1,3}$/;
// axe-core maps checkpoint tags without a delimiter, so a wcag success criteria/checkpoint
// of 1.2.3a becomes wcag123a. This maps dequeway data to be in a similar format
// for consistency purposes
const checkpointToTag = (checkpoint) => 
/* istanbul ignore next */
checkpoint ? `wcag${checkpoint.replace(/\./g, '')}` : '';
const findWcagTag = (data) => 
/* istanbul ignore next */
data.find(d => d.standards.find(t => WCAG_REGEX.test(t)))?.standards || [];
const allowedRuleTags = {
    'keyboard-inaccessible': 'wcag211a',
    'alt-text-essential-image-text': 'wcag111b',
    'alt-text-missing': 'wcag111b'
};
const getCheckpointTags = (data, rule) => {
    return data
        .map(d => !!d.checkpoint && d.standards.find(t => WCAG_REGEX.test(t))
        ? checkpointToTag(d.checkpoint)
        : '')
        .filter(Boolean)
        .filter(tag => !allowedRuleTags[rule] || allowedRuleTags[rule] === tag);
};
function mapAxeResultsToIssues(axeResults) {
    const issueMapper = (results, overrideProperties) => results
        .map(axeIssue => axeIssue.nodes.map(node => ({
        rule: axeIssue.id,
        description: axeIssue.description,
        helpText: axeIssue.help,
        helpUrl: axeIssue.helpUrl,
        impact: axeIssue.impact,
        potentialImpact: overrideProperties?.isNeedsReview
            ? axeIssue.impact
            : null,
        // TODO: This shouldn't be doing an "as string[]" cast.
        // See https://github.com/dequelabs/walnut/issues/8245
        selector: node.target,
        source: node.html,
        summary: node.failureSummary,
        tags: axeIssue.tags,
        isBestPractice: axeIssue.tags.includes('best-practice'),
        isNeedsReview: false,
        isManual: false,
        isAdvanced: axeIssue.tags.includes('advanced'),
        remediation: {
            any: node.any,
            all: node.all,
            none: node.none
        },
        createdAt: axeResults.timestamp,
        ...overrideProperties
    })))
        .flat();
    return [
        ...issueMapper(axeResults.violations),
        ...issueMapper(axeResults.incomplete, { isNeedsReview: true, impact: null })
    ];
}
exports.mapAxeResultsToIssues = mapAxeResultsToIssues;
// Note: Historically, we didn't always include tags for issues, or include checkpoint
// tags for dequeway issues. This ensures we always return something by doing a lookup
// for the respective issue
function getIssueTags({ rule, tags }) {
    if (Array.isArray(tags)) {
        return tags;
    }
    const axeRule = axe_core_1.default.getRules().find(auditRule => auditRule.ruleId === rule);
    if (axeRule?.tags?.length) {
        return axeRule.tags;
    }
    // Map dequeway wcag/checkpoint tags
    const issue = descriptions_json_1.default.find(({ id }) => id === rule);
    if (!issue) {
        return [];
    }
    return [
        ...findWcagTag(issue.data),
        ...getCheckpointTags(issue.data, rule)
    ];
}
exports.getIssueTags = getIssueTags;
function mapIssueToSavedIssue(issue, additionalProperties = {}) {
    const tags = getIssueTags(issue);
    return {
        rule: issue.rule,
        description: issue.description,
        helpText: issue.help,
        helpUrl: issue.help_url,
        impact: issue.needs_review ? null : issue.impact,
        potentialImpact: issue.needs_review ? issue.impact : null,
        selector: issue.selector,
        source: issue.source,
        summary: issue.summary,
        tags,
        isBestPractice: issue.variant === 'best-practice' || tags.includes('best-practice'),
        isAdvanced: tags.includes('advanced'),
        isNeedsReview: issue.needs_review,
        isManual: issue.is_manual,
        id: issue.id,
        testId: issue.test_id,
        testName: issue.test_name,
        testUrl: issue.test_url,
        foundBy: issue.found_by,
        createdAt: issue.created_at,
        updatedAt: issue.updated_at,
        remediation: issue.remediation,
        igtTool: issue.manifest_guide,
        sharedWith: issue.shared_with,
        screenshotId: issue.screenshot_id,
        boundingBox: issue.bounding_box,
        manifestId: issue.manifest_id,
        integrationUrl: issue.integration_url,
        ...additionalProperties
    };
}
exports.mapIssueToSavedIssue = mapIssueToSavedIssue;
const mapAxeExtensionIssueToUniversalIssue = (issue) => {
    // Note: It shouldn't be necessary to fall back to `createdAt` here, and
    // should be removed once we're confident that `createdAt` is always present.
    const createdAt = new Date().toISOString();
    const tags = getIssueTags(issue);
    const guideName = (0, igt_name_map_1.getIgtName)(issue.igtTool);
    return {
        issueId: issue.id || null,
        ruleId: issue.rule,
        description: issue.description,
        help: issue.helpText,
        helpUrl: issue.helpUrl,
        impact: issue.impact,
        needsReview: issue.isNeedsReview,
        isManual: issue.isManual,
        isAdvanced: issue.tags?.includes('advanced') || false,
        selector: issue.selector,
        summary: issue.summary,
        source: issue.source,
        tags,
        igt: guideName,
        testName: issue.testName,
        shareURL: issue.shareURL || null,
        createdAt: issue.createdAt
            ? new Date(issue.createdAt).toISOString()
            : createdAt,
        isExperimental: tags.includes('experimental'),
        foundBy: issue.foundBy,
        manifestId: issue.manifestId,
        integrationUrl: issue.integrationUrl || null,
        igtId: issue.igtTool,
        groupId: issue.testId
    };
};
exports.mapAxeExtensionIssueToUniversalIssue = mapAxeExtensionIssueToUniversalIssue;
const mapDedupedResultToUniversalIssue = (result, unique) => {
    const { id, impact, tags, description, help, helpUrl, duplicates } = result;
    return {
        issueId: null,
        ruleId: id,
        description,
        help,
        helpUrl,
        impact,
        needsReview: false,
        isExperimental: false,
        isManual: false,
        isAdvanced: false,
        summary: unique.failureSummary,
        shareURL: null,
        // TODO: This shouldn't be doing an "as string[]" cast.
        // See https://github.com/dequelabs/walnut/issues/8245
        selector: unique.target,
        source: unique.html,
        tags,
        createdAt: new Date(unique.created_at).toISOString(),
        screenshotId: unique.screenshot_id || null,
        boundingBox: unique.advanced?.visibleBoundingRect ?? null,
        testUrl: unique.url,
        testPageTitle: unique.page_state || '',
        duplicates,
        integrationUrl: null
    };
};
const mapDedupedResultsToUniversalIssue = (watcherResults) => watcherResults.flatMap(result => {
    return result.uniques.map(unique => mapDedupedResultToUniversalIssue(result, unique));
});
exports.mapDedupedResultsToUniversalIssue = mapDedupedResultsToUniversalIssue;
const mapDedupedPageStatesToUniversalIssue = (pageStates) => 
/* Uses flatMap to flatten the resulting array structure from mapping each pageState and result */
pageStates.flatMap(pageState => pageState.result.flatMap(result => {
    return result.uniques.map(unique => ({
        ...mapDedupedResultToUniversalIssue(result, unique),
        pageStateKey: (0, build_page_state_key_1.buildPageStateKey)(pageState),
        isNewIssue: unique.isNewIssue,
        groupId: (0, build_page_state_key_1.buildPageStateKey)(pageState)
    }));
}));
exports.mapDedupedPageStatesToUniversalIssue = mapDedupedPageStatesToUniversalIssue;
//# sourceMappingURL=issue-mapper.js.map